import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { CreateUserParams, useCreateUserMutation } from 'entities/User';
import { useCallback, useState } from 'react';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import { isErrorApiResponse } from 'shared/utils/helpers/isErrorApiResponse';

interface HookApi {
  isOpenedConfirm: boolean;
  isLoading: boolean;
  createUser: (userData: CreateUserParams) => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
}

export const useCreateUser = (onSuccess?: () => void): HookApi => {
  const { t } = useAppTranslation('common');

  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const [createUserService, { isLoading }] = useCreateUserMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const createUser = useCallback(
    async (userData: CreateUserParams): Promise<void> => {
      try {
        await createUserService({
          ...userData,
          identificationNumber: userData.identificationNumber || null,
          dateOfBirth: userData.dateOfBirth ? getServerDateFormat(userData.dateOfBirth) : undefined,
        }).unwrap();

        showNotification('info', t('Success'), t('User has been successfully created'));
        onSuccess?.();
      } catch (error) {
        if (isErrorApiResponse(error)) {
          const errorMessage = Array.isArray(error.data.message)
            ? error.data.message.map((message) => t(message, { ns: 'common' }))
            : t(error.data.message, { ns: 'common' });
          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        } else {
          console.log(error);
        }
      } finally {
        closeConfirmModal();
      }
    },
    [closeConfirmModal, createUserService, onSuccess, t],
  );

  return {
    openConfirmModal,
    closeConfirmModal,
    createUser,
    isLoading,
    isOpenedConfirm,
  };
};
